import { Download, Eye, EyeOff, Upload } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
	AppEnvClient,
	AppEnvListResult,
	AppEnvManifestEntry,
	AppEnvSaveEntry,
	AppEnvValueType,
} from '../../lib/app-env/types';
import { diffEntries, parseEnvBlob, serializeEnvBlob } from '../../lib/app-env/parse-env';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface AppEnvPanelProps {
	/** `installed_apps.id` from core (Decision 9e). */
	installedAppId: string;
	/** Manifest-declared env schema. Empty array → empty-state message. */
	schema: AppEnvManifestEntry[];
	/** SDK client. When Group B publishes, pass the `@khal-os/sdk` instance. */
	client: AppEnvClient;
	/** Whether the current user has admin role — gates export + ad-hoc keys. */
	isAdmin?: boolean;
	/** Optional callback for the host shell — fires after a successful save. */
	onSaved?: () => void;
}

interface FieldDraft {
	key: string;
	value: string;
	type: AppEnvValueType;
	dirty: boolean;
	revealed?: boolean;
	error?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function inferType(entry: AppEnvManifestEntry | undefined): AppEnvValueType {
	return entry?.type ?? 'string';
}

function validateField(value: string, type: AppEnvValueType, required: boolean): string | undefined {
	if (required && value.trim() === '') return 'Required';
	if (value === '') return undefined;
	if (type === 'url') {
		try {
			new URL(value);
		} catch {
			return 'Invalid URL';
		}
	}
	if (type === 'number' && Number.isNaN(Number(value))) return 'Must be a number';
	return undefined;
}

function buildDrafts(
	schema: AppEnvManifestEntry[],
	list: AppEnvListResult | null
): FieldDraft[] {
	const valuesByKey = new Map((list?.values ?? []).map((v) => [v.key, v]));
	const schemaByKey = new Map(schema.map((s) => [s.key, s]));
	const out: FieldDraft[] = [];

	for (const declared of schema) {
		const stored = valuesByKey.get(declared.key);
		out.push({
			key: declared.key,
			value: stored?.masked_value ?? declared.default ?? '',
			type: inferType(declared),
			dirty: false,
		});
	}
	// Ad-hoc rows: keys present server-side but not in the manifest.
	for (const stored of list?.values ?? []) {
		if (!schemaByKey.has(stored.key)) {
			out.push({
				key: stored.key,
				value: stored.masked_value ?? '',
				type: stored.type,
				dirty: false,
			});
		}
	}
	return out;
}

function schemaFingerprint(schema: AppEnvManifestEntry[]): string {
	return JSON.stringify(
		schema.map((entry) => ({
			key: entry.key,
			type: entry.type,
			required: entry.required ?? false,
			default: entry.default ?? '',
			description: entry.description ?? '',
		}))
	);
}

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------

export function AppEnvPanel({
	installedAppId,
	schema,
	client,
	isAdmin = false,
	onSaved,
}: AppEnvPanelProps) {
	const [list, setList] = useState<AppEnvListResult | null>(null);
	const [drafts, setDrafts] = useState<FieldDraft[]>([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState<string | null>(null);
	const [importOpen, setImportOpen] = useState(false);
	const schemaKey = useMemo(() => schemaFingerprint(schema), [schema]);
	const stableSchema = useMemo(() => schema, [schemaKey]);

	const refresh = useCallback(async () => {
		setLoading(true);
		try {
			const result = await client.list({ installedAppId });
			setList(result);
			setDrafts(buildDrafts(stableSchema, result));
		} finally {
			setLoading(false);
		}
	}, [client, installedAppId, stableSchema]);

	useEffect(() => {
		refresh();
	}, [refresh]);

	const dirtyCount = useMemo(() => drafts.filter((d) => d.dirty).length, [drafts]);

	const updateDraft = useCallback(
		(key: string, patch: Partial<FieldDraft>) => {
			setDrafts((prev) =>
				prev.map((d) => {
					if (d.key !== key) return d;
					const next = { ...d, ...patch, dirty: true };
					const declared = stableSchema.find((s) => s.key === key);
					next.error = validateField(next.value, next.type, declared?.required ?? false);
					return next;
				})
			);
		},
		[stableSchema]
	);

	const handleReveal = useCallback(
		async (key: string) => {
			const draft = drafts.find((d) => d.key === key);
			if (!draft) return;
			if (draft.revealed) {
				setDrafts((prev) => prev.map((d) => (d.key === key ? { ...d, revealed: false } : d)));
				return;
			}
			try {
				const { value } = await client.reveal({ installedAppId, key });
				setDrafts((prev) =>
					prev.map((d) => (d.key === key ? { ...d, value, revealed: true } : d))
				);
			} catch (err) {
				setSaveError(err instanceof Error ? err.message : 'Reveal failed');
			}
		},
		[client, drafts, installedAppId]
	);

	const handleSave = useCallback(async () => {
		const dirty = drafts.filter((d) => d.dirty);
		if (dirty.length === 0) return;
		const errors = dirty.filter((d) => d.error);
		if (errors.length > 0) {
			setSaveError(`Fix ${errors.length} field error${errors.length === 1 ? '' : 's'} first`);
			return;
		}
		const entries: AppEnvSaveEntry[] = dirty.map((d) => ({
			key: d.key,
			value: d.value,
			type: d.type,
		}));
		setSaving(true);
		setSaveError(null);
		try {
			await client.save({ installedAppId, entries });
			onSaved?.();
			await refresh();
		} catch (err) {
			setSaveError(err instanceof Error ? err.message : 'Save failed');
		} finally {
			setSaving(false);
		}
	}, [client, drafts, installedAppId, onSaved, refresh]);

	const handleClaimAdopt = useCallback(async () => {
		if (!list?.claim_pending) return;
		try {
			await client.claim({ installedAppId, sourceKeys: list.claim_keys });
			await refresh();
		} catch (err) {
			setSaveError(err instanceof Error ? err.message : 'Claim failed');
		}
	}, [client, installedAppId, list, refresh]);

	const handleClaimSkip = useCallback(async () => {
		try {
			await client.claim_skip({ installedAppId });
			setList((prev) => (prev ? { ...prev, claim_pending: false, claim_keys: [] } : prev));
		} catch (err) {
			setSaveError(err instanceof Error ? err.message : 'Skip failed');
		}
	}, [client, installedAppId]);

	const handleImport = useCallback(
		(blob: string) => {
			const parsed = parseEnvBlob(blob);
			if (parsed.entries.length === 0) {
				setSaveError(parsed.errors[0]?.message ?? 'No entries found');
				return;
			}
			setDrafts((prev) => {
				const next = [...prev];
				for (const { key, value } of parsed.entries) {
					const idx = next.findIndex((d) => d.key === key);
					const declared = stableSchema.find((s) => s.key === key);
					const type = declared?.type ?? 'string';
					if (idx >= 0) {
						next[idx] = { ...next[idx]!, value, dirty: true, type };
					} else if (isAdmin) {
						next.push({ key, value, type, dirty: true });
					}
				}
				return next;
			});
			setImportOpen(false);
		},
		[isAdmin, stableSchema]
	);

	const handleExport = useCallback(async () => {
		if (!client.export) {
			setSaveError('Export not available');
			return;
		}
		try {
			const { entries } = await client.export({ installedAppId });
			const blob = serializeEnvBlob(entries);
			const url = URL.createObjectURL(new Blob([blob], { type: 'text/plain' }));
			const a = document.createElement('a');
			a.href = url;
			a.download = `${installedAppId}.env`;
			a.click();
			URL.revokeObjectURL(url);
		} catch (err) {
			setSaveError(err instanceof Error ? err.message : 'Export failed');
		}
	}, [client, installedAppId]);

	if (loading) {
		return (
			<div style={{ fontSize: 12, color: 'var(--khal-text-muted)', padding: '8px 0' }}>Loading environment…</div>
		);
	}

	if (stableSchema.length === 0 && (list?.values?.length ?? 0) === 0 && !list?.claim_pending && !isAdmin) {
		return (
			<div style={{ fontSize: 12, color: 'var(--khal-text-muted)', padding: '8px 0' }}>
				This app has no configurable environment variables.
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-3">
			{list?.claim_pending && (
				<ClaimBanner
					keys={list.claim_keys}
					modifiedAt={list.claim_secret_modified_at}
					onAdopt={handleClaimAdopt}
					onSkip={handleClaimSkip}
				/>
			)}

			{/* Toolbar */}
			<div className="flex items-center justify-between" data-testid="appenv-toolbar">
				<div className="flex items-center gap-2" style={{ fontSize: 11, color: 'var(--khal-text-muted)' }}>
					{dirtyCount > 0 && (
						<span
							data-testid="appenv-dirty-badge"
							className="rounded-full px-1.5"
							style={{ background: 'var(--khal-accent-blue)', color: 'white' }}
						>
							{dirtyCount} unsaved
						</span>
					)}
				</div>
				<div className="flex items-center gap-1.5">
					<ToolbarButton onClick={() => setImportOpen(true)} icon={<Upload size={11} />}>
						Import .env
					</ToolbarButton>
					{isAdmin && client.export && (
						<ToolbarButton onClick={handleExport} icon={<Download size={11} />}>
							Export .env
						</ToolbarButton>
					)}
				</div>
			</div>

			{/* Field list */}
			<div className="flex flex-col gap-2">
				{drafts.map((draft) => {
					const declared = stableSchema.find((s) => s.key === draft.key);
					return (
						<EnvField
							key={draft.key}
							draft={draft}
							declared={declared}
							onChange={(value) => updateDraft(draft.key, { value })}
							onReveal={() => handleReveal(draft.key)}
						/>
					);
				})}
			</div>

			{/* Save row */}
			<div className="flex items-center justify-between pt-2" style={{ borderTop: '1px solid var(--khal-border-default)' }}>
				<span style={{ fontSize: 11, color: saveError ? 'var(--khal-status-error)' : 'var(--khal-text-muted)' }}>
					{saveError ?? (dirtyCount > 0 ? 'Unsaved changes — pod restarts on save.' : '')}
				</span>
				<button
					type="button"
					data-testid="appenv-save"
					disabled={saving || dirtyCount === 0}
					onClick={handleSave}
					className="rounded px-3 py-1 text-[11px] font-semibold"
					style={{
						background: dirtyCount > 0 ? 'var(--khal-accent-blue)' : 'var(--khal-controls-bg)',
						color: dirtyCount > 0 ? 'white' : 'var(--khal-text-muted)',
						opacity: saving ? 0.6 : 1,
					}}
				>
					{saving ? 'Saving…' : 'Save'}
				</button>
			</div>

			{importOpen && <ImportModal onCancel={() => setImportOpen(false)} onConfirm={handleImport} />}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function EnvField({
	draft,
	declared,
	onChange,
	onReveal,
}: {
	draft: FieldDraft;
	declared: AppEnvManifestEntry | undefined;
	onChange: (value: string) => void;
	onReveal: () => void;
}) {
	const isSecret = draft.type === 'secret';
	const inputType = isSecret && !draft.revealed ? 'password' : draft.type === 'url' ? 'url' : 'text';
	return (
		<div
			className="flex flex-col gap-1 rounded border p-2"
			style={{ borderColor: draft.error ? 'var(--khal-status-error)' : 'var(--khal-border-default)' }}
		>
			<div className="flex items-center justify-between gap-2">
				<label
					htmlFor={`appenv-field-${draft.key}`}
					className="font-mono"
					style={{ fontSize: 11, color: 'var(--khal-text-primary)', fontWeight: 600 }}
				>
					{draft.key}
					{declared?.required && <span style={{ color: 'var(--khal-status-error)', marginLeft: 4 }}>*</span>}
				</label>
				<span
					className="rounded px-1.5 py-0.5"
					style={{
						fontSize: 9,
						color: 'var(--khal-text-muted)',
						background: 'var(--khal-surface-default)',
						border: '1px solid var(--khal-border-default)',
					}}
				>
					{draft.type}
				</span>
			</div>
			{declared?.description && (
				<span style={{ fontSize: 10, color: 'var(--khal-text-muted)' }}>{declared.description}</span>
			)}
			<div className="flex items-center gap-1.5">
				<input
					id={`appenv-field-${draft.key}`}
					data-testid={`appenv-field-${draft.key}`}
					type={inputType}
					value={draft.value}
					onChange={(e) => onChange(e.target.value)}
					className="w-full rounded px-2 py-1"
					style={{
						fontSize: 12,
						background: 'var(--khal-controls-bg)',
						color: 'var(--khal-text-primary)',
						border: '1px solid var(--khal-border-default)',
					}}
				/>
				{isSecret && (
					<button
						type="button"
						aria-label={draft.revealed ? 'Hide secret' : 'Reveal secret'}
						data-testid={`appenv-reveal-${draft.key}`}
						onClick={onReveal}
						className="flex h-6 w-6 items-center justify-center rounded"
						style={{ color: 'var(--khal-text-muted)', border: '1px solid var(--khal-border-default)' }}
					>
						{draft.revealed ? <EyeOff size={12} /> : <Eye size={12} />}
					</button>
				)}
			</div>
			{draft.error && (
				<span data-testid={`appenv-error-${draft.key}`} style={{ fontSize: 10, color: 'var(--khal-status-error)' }}>
					{draft.error}
				</span>
			)}
		</div>
	);
}

function ClaimBanner({
	keys,
	modifiedAt,
	onAdopt,
	onSkip,
}: {
	keys: string[];
	modifiedAt?: string;
	onAdopt: () => void;
	onSkip: () => void;
}) {
	return (
		<div
			data-testid="appenv-claim-banner"
			className="flex flex-col gap-2 rounded border p-2"
			style={{
				background: 'rgba(234,179,8,0.08)',
				borderColor: 'rgba(234,179,8,0.4)',
				color: 'var(--khal-status-warning)',
				fontSize: 11,
			}}
		>
			<span>
				This app already has env values set outside this UI
				{modifiedAt && ` (last modified ${new Date(modifiedAt).toLocaleString()})`}. Adopt them so they appear here?
			</span>
			<details>
				<summary style={{ cursor: 'pointer', fontSize: 10 }}>{keys.length} keys</summary>
				<div className="font-mono" style={{ fontSize: 10, color: 'var(--khal-text-muted)' }}>
					{keys.join(', ')}
				</div>
			</details>
			<div className="flex gap-1.5">
				<button
					type="button"
					data-testid="appenv-claim-adopt"
					onClick={onAdopt}
					className="rounded px-2 py-0.5 text-[11px] font-semibold"
					style={{ background: 'var(--khal-status-warning)', color: 'var(--khal-bg)' }}
				>
					Adopt
				</button>
				<button
					type="button"
					data-testid="appenv-claim-skip"
					onClick={onSkip}
					className="rounded px-2 py-0.5 text-[11px]"
					style={{ background: 'var(--khal-controls-bg)', color: 'var(--khal-text-muted)' }}
				>
					Skip
				</button>
			</div>
		</div>
	);
}

function ImportModal({
	onCancel,
	onConfirm,
}: {
	onCancel: () => void;
	onConfirm: (blob: string) => void;
}) {
	const [text, setText] = useState('');
	const parsed = useMemo(() => parseEnvBlob(text), [text]);
	const proposed = parsed.entries.map((e) => ({ key: e.key, value: e.value }));
	const diff = diffEntries([], proposed);
	return (
		<div
			data-testid="appenv-import-modal"
			className="flex flex-col gap-2 rounded border p-2"
			style={{ background: 'var(--khal-surface-raised)', borderColor: 'var(--khal-border-default)' }}
		>
			<span style={{ fontSize: 11, color: 'var(--khal-text-primary)', fontWeight: 600 }}>
				Paste a .env blob
			</span>
			<textarea
				data-testid="appenv-import-textarea"
				value={text}
				onChange={(e) => setText(e.target.value)}
				rows={6}
				className="w-full rounded p-2 font-mono"
				style={{
					fontSize: 11,
					background: 'var(--khal-controls-bg)',
					color: 'var(--khal-text-primary)',
					border: '1px solid var(--khal-border-default)',
				}}
				placeholder={'KEY=value\nOTHER="quoted value"'}
			/>
			<div style={{ fontSize: 10, color: 'var(--khal-text-muted)' }}>
				{parsed.entries.length} entries · {parsed.errors.length} errors · {diff.added.length} new
			</div>
			{parsed.errors.length > 0 && (
				<ul style={{ fontSize: 10, color: 'var(--khal-status-error)' }}>
					{parsed.errors.slice(0, 3).map((e, i) => (
						<li key={i}>
							line {e.line}: {e.message}
						</li>
					))}
				</ul>
			)}
			<div className="flex justify-end gap-1.5">
				<button
					type="button"
					data-testid="appenv-import-cancel"
					onClick={onCancel}
					className="rounded px-2 py-0.5 text-[11px]"
					style={{ background: 'var(--khal-controls-bg)', color: 'var(--khal-text-muted)' }}
				>
					Cancel
				</button>
				<button
					type="button"
					data-testid="appenv-import-confirm"
					disabled={parsed.entries.length === 0}
					onClick={() => onConfirm(text)}
					className="rounded px-2 py-0.5 text-[11px] font-semibold"
					style={{
						background: 'var(--khal-accent-blue)',
						color: 'white',
						opacity: parsed.entries.length === 0 ? 0.5 : 1,
					}}
				>
					Stage {parsed.entries.length} {parsed.entries.length === 1 ? 'change' : 'changes'}
				</button>
			</div>
		</div>
	);
}

function ToolbarButton({
	onClick,
	icon,
	children,
}: {
	onClick: () => void;
	icon: React.ReactNode;
	children: React.ReactNode;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className="flex items-center gap-1 rounded px-2 py-0.5 text-[11px]"
			style={{
				background: 'var(--khal-controls-bg)',
				color: 'var(--khal-text-muted)',
				border: '1px solid var(--khal-border-default)',
			}}
		>
			{icon}
			{children}
		</button>
	);
}
